/**
 * ScheduleSection -- 식순 편집
 */

import { ArrowUpDown, ListOrdered, Plus, X } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Input, Textarea, Button, TimePicker } from '../../ui/index.js'
import type { ScheduleItem } from '@cugumi/shared/schemas'
import styles from './SectionForm.module.css'

/** "HH:MM" → "오전/오후 h:mm" */
function formatTime12(time: string): string {
  const match = time.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return time
  const h = parseInt(match[1], 10)
  const m = match[2]
  const period = h < 12 ? '오전' : '오후'
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${period} ${display}:${m}`
}

export function ScheduleSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  const items: ScheduleItem[] = (invitation as any).schedule ?? []

  function setItems(next: ScheduleItem[]) {
    updateNested('schedule', next)
  }

  function addItem() {
    setItems([...items, { time: '', title: '', description: '' }])
  }

  function removeItem(index: number) {
    setItems(items.filter((_, i) => i !== index))
  }

  function updateItem(index: number, field: keyof ScheduleItem, value: string) {
    const next = items.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    setItems(next)
  }

  function sortItems() {
    const sorted = [...items].sort((a, b) => a.time.localeCompare(b.time))
    setItems(sorted)
  }

  return (
    <div className={styles.form}>
      <div className={styles.personCard}>
        <div className={styles.personCardHeader}>
          <span className={styles.personCardIconBadge}>
            <ListOrdered size={20} />
          </span>
          <span className={styles.personCardLabel}>식순 항목</span>
          <div className={styles.personCardHeaderActions}>
            {items.length >= 2 && (
              <Button variant="ghost" size="sm" onClick={sortItems}>
                <ArrowUpDown size={14} /> 정렬
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={addItem}>
              <Plus size={14} /> 추가
            </Button>
          </div>
        </div>
        <div className={styles.personCardDivider} />
        <div className={styles.personCardBody}>
          {items.length === 0 && (
            <p className={styles.hint}>식순 항목을 추가하세요</p>
          )}

          {items.map((item, i) => (
            <div key={i} className={styles.fieldGroup}>
              <div className={styles.itemHeader}>
                <div className={styles.itemLabelGroup}>
                  <span className={styles.itemLabel}>항목 {i + 1}</span>
                  {item.time && <span className={styles.itemTime}>· {formatTime12(item.time)}</span>}
                </div>
                <button
                  type="button"
                  className={styles.itemDeleteBtn}
                  onClick={() => removeItem(i)}
                  aria-label={`항목 ${i + 1} 삭제`}
                >
                  <X size={14} />
                </button>
              </div>
              <div className={styles.row}>
                <TimePicker
                  label="시간"
                  value={item.time}
                  onChange={(v) => updateItem(i, 'time', v)}
                  placeholder="시간 선택"
                />
                <Input
                  label="제목"
                  value={item.title}
                  onChange={(e) => updateItem(i, 'title', e.target.value)}
                  placeholder="본식"
                />
              </div>
              <Textarea
                label="설명"
                value={item.description ?? ''}
                onChange={(e) => updateItem(i, 'description', e.target.value)}
                placeholder="본식이 진행됩니다"
                rows={2}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
